import { useCallback, useEffect, useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import { Button, Flex, Heading, Switch, Text } from '@radix-ui/themes'
import { DataTestIDs } from 'shared/constants'
import { XyroDialog } from 'shared/ui'
import { useConsent } from '../hooks/use-consent'
import { consentVar, isConsentDialogOpenVar } from '../store'
import type { ConsentCategories, ConsentCategory } from '../types'
import styles from '../consent.module.scss'

const CATEGORIES: Array<{
  id: ConsentCategory
  title: string
  description: string
  testId: DataTestIDs
}> = [
  {
    id: 'analytics',
    title: 'Analytics',
    description:
      'How the games are used, so we can improve them. Sent to our own servers and analytics tools.',
    testId: DataTestIDs.switchConsentAnalytics
  },
  {
    id: 'marketing',
    title: 'Marketing',
    description:
      'Lets us measure which ads brought you here. Shared with ad platforms.',
    testId: DataTestIDs.switchConsentMarketing
  }
]

const NONE: ConsentCategories = { analytics: false, marketing: false }

/** Per-category cookie preferences (FE-330); opened from the banner and settings. */
export const ConsentDialog = () => {
  const isOpen = useReactiveVar(isConsentDialogOpenVar)
  const current = useReactiveVar(consentVar)
  const { grant } = useConsent()
  const [draft, setDraft] = useState<ConsentCategories>(NONE)

  useEffect(() => {
    if (isOpen) setDraft(current ?? NONE)
  }, [isOpen, current])

  const handleOpenChange = useCallback((nextIsOpen: boolean) => {
    isConsentDialogOpenVar(nextIsOpen)
  }, [])

  const handleSave = useCallback(() => grant(draft), [grant, draft])

  return (
    <XyroDialog
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <Flex
        className={styles.dialogBody}
        direction='column'
        gap='3'
        data-testid={DataTestIDs.consentDialog}
      >
        <Heading
          size='5'
          className='color-white'
        >
          Cookie preferences
        </Heading>

        <Flex
          className={styles.category}
          justify='between'
          align='center'
          gap='4'
        >
          <Flex
            direction='column'
            gap='1'
          >
            <Text
              size='3'
              weight='medium'
              className='color-white'
            >
              Necessary
            </Text>
            <Text
              size='2'
              className='color-gray-light'
            >
              Session, sign-in (Privy) and your preferences. Always on.
            </Text>
          </Flex>
          <Text
            size='2'
            weight='medium'
            className='color-gray-light'
            style={{ flexShrink: 0 }}
          >
            Always on
          </Text>
        </Flex>

        {CATEGORIES.map(category => (
          <Flex
            key={category.id}
            className={styles.category}
            justify='between'
            align='center'
            gap='4'
          >
            <Flex
              direction='column'
              gap='1'
            >
              <Text
                size='3'
                weight='medium'
                className='color-white'
              >
                {category.title}
              </Text>
              <Text
                size='2'
                className='color-gray-light'
              >
                {category.description}
              </Text>
            </Flex>
            <Switch
              size='2'
              radius='full'
              color='yellow'
              checked={draft[category.id]}
              onCheckedChange={checked =>
                setDraft(prev => ({ ...prev, [category.id]: checked }))
              }
              data-testid={category.testId}
            />
          </Flex>
        ))}

        <Button
          color='yellow'
          size='3'
          type='button'
          mt='3'
          onClick={handleSave}
          data-testid={DataTestIDs.buttonConsentSave}
        >
          Save preferences
        </Button>
      </Flex>
    </XyroDialog>
  )
}
